import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Button, Card, Row, Col, Typography, Divider, Empty } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { globalProductSearch } from '../../redux/actions/product/globalSearchAction';
import { debounce } from '../../utils/debounce';

const { Text, Title } = Typography;

const ProductSearchModal = ({ 
  visible, 
  onClose, 
  allProducts, 
  onProductSelect,
  onAddNewProduct,
  selectedProds = [] // Add selectedProds to track already selected products
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // Track multiple selections
  const dispatch = useDispatch();

  // Initialize filtered products when modal opens
  useEffect(() => {
    if (visible && allProducts) {
      setFilteredProducts(allProducts);
      setSearchValue('');
      setSelectedItems([]); // Reset selections when modal opens
    }
  }, [visible, allProducts]);

  // Debounced server-side search function
  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') {
        setFilteredProducts(allProducts || []);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Use Redux action for global search with pagination
        const result = await dispatch(globalProductSearch(searchTerm.trim(), 1, 100));
        if (result && result.status === "success") {
          setFilteredProducts(result.data || []);
        }
      } catch (error) {
        console.error('Search error:', error);
        // Fallback to client-side filtering
        const filtered = (allProducts || []).filter(product => 
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.book_publisher?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredProducts(filtered);
      } finally {
        setLoading(false);
      }
    }, 500),
    [allProducts, dispatch]
  );

  // Handle search input
  const handleSearch = (value) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleProductClick = (product) => {
    // Toggle selection
    setSelectedItems(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleAddSelectedProducts = () => {
    // Add all selected products with unique keys
    // Generate unique keys for each product to prevent key collisions
    // Use a base timestamp and increment for each product to ensure uniqueness
    const baseTimestamp = Date.now();
    selectedItems.forEach((product, index) => {
      // Generate unique key using base timestamp, index, product ID, and random
      // This ensures each product gets a truly unique key even when added in bulk
      const uniqueKey = baseTimestamp + index + (product.id * 1000000) + Math.random() * 1000;
      onProductSelect(product, uniqueKey);
    });
    onClose();
  };

  const handleAddNewProduct = () => {
    onAddNewProduct();
    onClose();
  };

  return (
    <Modal
      title={`Add Product to Invoice ${selectedItems.length > 0 ? `(${selectedItems.length} selected)` : ''}`}
      open={visible}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="primary" 
            onClick={handleAddSelectedProducts}
            disabled={selectedItems.length === 0}
          >
            Add {selectedItems.length > 0 ? `${selectedItems.length} Product${selectedItems.length > 1 ? 's' : ''}` : 'Products'}
          </Button>
        </div>
      }
      width="90%"
      style={{ maxWidth: '600px' }}
      bodyStyle={{ padding: '16px' }}
    >
      {/* Search Input */}
      <div className="mb-3">
        <Input
          size="large"
          placeholder="Search by ISBN, Title, Author, or Publisher"
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ marginBottom: '16px' }}
          loading={loading}
        />
      </div>

      {/* Add New Product Button */}
      <div className="mb-3">
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddNewProduct}
          style={{ width: '100%', height: '40px' }}
        >
          Add New Product
        </Button>
      </div>

      <Divider />

      {/* Products List */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text>Searching...</Text>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="row">
            {filteredProducts.map((product) => {
              const isSelected = selectedItems.some(p => p.id === product.id);
              const isAlreadyInInvoice = selectedProds.some(p => p.id === product.id);
              
              return (
                <div key={product.id} className="col-12 mb-2">
                  <Card
                    size="small"
                    hoverable
                    onClick={() => handleProductClick(product)}
                    style={{ 
                      cursor: 'pointer',
                      border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      backgroundColor: isSelected ? '#e6f7ff' : 'white',
                      opacity: isAlreadyInInvoice ? 0.6 : 1
                    }}
                  >
                  <div className="row align-items-center">
                    {/* Product Image */}
                    <div className="col-2">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#999'
                          }}
                        >
                          No Image
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="col-10">
                      <div className="d-flex justify-content-between align-items-start">
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ fontSize: '14px', display: 'block' }}>
                            {product.name}
                            {isAlreadyInInvoice && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '11px', 
                                color: '#52c41a',
                                fontWeight: 'normal'
                              }}>
                                ✓ Already in invoice
                              </span>
                            )}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                            ISBN: {product.isbn}
                          </Text>
                          {product.author && (
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              Author: {product.author}
                            </Text>
                          )}
                          {product.book_publisher?.name && (
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              Publisher: {product.book_publisher.name}
                            </Text>
                          )}
                        </div>
                        
                        {/* Price and Stock */}
                        <div style={{ textAlign: 'right', minWidth: '80px' }}>
                          <Text strong style={{ fontSize: '14px', display: 'block' }}>
                            ₹{product.sale_price || 0}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                            Stock: {product.quantity || 0}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
              );
            })}
          </div>
        ) : (
          <Empty
            description={
              searchValue ? 
                `No products found for "${searchValue}"` : 
                "No products available"
            }
            style={{ padding: '40px 0' }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNewProduct}>
              Add New Product
            </Button>
          </Empty>
        )}
      </div>

      {/* Footer Info */}
      {filteredProducts.length > 0 && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default ProductSearchModal;
